import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ResponseModel, { PaginationRes } from "../../../models/response.model";
import {
  getBOMComponentsApi,
  getEntitiesWithBOMApi,
} from "../../../api/copyBom.api";

interface Component {
  type: string;
  id: string;
  name: string;
  quantity: number;
  unitValue: string;
  unitLabel: string;
}

interface Entity {
  id: string;
  name: string;
  bomId: string;
  unit?: string;
  unitType?: string;
}

interface CopyBomState {
  entities: Entity[];
  components: Component[];
  paginationEntities?: PaginationRes;
  loading: {
    getEntities: boolean;
    getMaterials: boolean;
  };
}

const initialState: CopyBomState = {
  entities: [],
  components: [],
  loading: {
    getEntities: false,
    getMaterials: false,
  },
};

export const getEntities = createAsyncThunk<
  ResponseModel,
  {
    params: {
      page: number;
      limit: number;
      from: string;
      search?: string;
    };
  },
  { rejectValue: string }
>("copyBom/getEntities", async ({ params }, { rejectWithValue }) => {
  try {
    return await getEntitiesWithBOMApi(params);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});

export const getComponents = createAsyncThunk<
  ResponseModel,
  {
    params: {
      from: string;
      bomId: string;
    };
  },
  { rejectValue: string }
>("copyBom/getComponents", async ({ params }, { rejectWithValue }) => {
  try {
    return await getBOMComponentsApi(params.bomId, params.from);
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch report",
    );
  }
});

const copyBomSlice = createSlice({
  name: "copyBom",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEntities.pending, (state) => {
        state.loading.getEntities = true;
      })
      .addCase(getEntities.fulfilled, (state, action) => {
        state.loading.getEntities = false;
        state.entities = action.payload.data;
        state.paginationEntities = action.payload.pagination;
      })
      .addCase(getEntities.rejected, (state, action) => {
        state.loading.getEntities = false;
      })
      .addCase(getComponents.pending, (state) => {
        state.loading.getMaterials = true;
      })
      .addCase(getComponents.fulfilled, (state, action) => {
        state.loading.getMaterials = false;
        state.components = action.payload.data;
      })
      .addCase(getComponents.rejected, (state, action) => {
        state.loading.getMaterials = false;
      });
  },
});

export default copyBomSlice.reducer;
